import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { GoogleSignInButton } from '@/components/GoogleSignInButton';

const AdminLogin = () => {
    const navigate = useNavigate();
    const { login, isAuthenticated } = useAuth();
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // Redirect if already authenticated
    useEffect(() => {
        if (isAuthenticated) {
            navigate('/admin', { replace: true });
        }
    }, [isAuthenticated, navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await login(formData);
            console.log('✅ Login complete, token set in localStorage');
            
            // Force a small delay to ensure localStorage is synced
            await new Promise(resolve => setTimeout(resolve, 50));
            
            // Navigate to admin
            navigate('/admin', { replace: true });
        } catch (err: any) {
            console.error('❌ Login failed:', err);
            setError(err.response?.data?.detail || 'Invalid credentials. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-vintage-cream paper-texture flex items-center justify-center px-4">
            <div className="max-w-md w-full">
                {/* Back Link - Moved to top */}
                <div className="mb-6 text-center">
                    <button
                        onClick={() => navigate('/')}
                        className="text-vintage-brown hover:text-vintage-darkbrown text-sm font-body inline-flex items-center"
                    >
                        ← Back to Home
                    </button>
                </div>

                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-serif font-bold text-vintage-ink mb-2">Admin Login</h1>
                    <p className="text-vintage-darkbrown font-body">For write to My Radiant Lamp</p>
                </div>

                {/* Login Form */}
                <div className="letter-content">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Email */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-serif font-semibold text-vintage-darkbrown mb-2">
                                Email Address
                            </label>
                            <input
                                id="email"
                                type="email"
                                required
                                className="input-field"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="admin@sincerelyyourcaffeine.com"
                            />
                        </div>

                        {/* Password */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-serif font-semibold text-vintage-darkbrown mb-2">
                                Password
                            </label>
                            <input
                                id="password"
                                type="password"
                                required
                                className="input-field"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                placeholder="••••••••"
                            />
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
                                {error}
                            </div>
                        )}

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Logging in...' : 'Login'}
                        </button>
                    </form>

                    {/* Divider */}
                    <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-vintage-brown/20" />
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-2 bg-vintage-paper text-vintage-brown font-body">Or</span>
                        </div>
                    </div>

                    {/* Google Sign-In Button */}
                    <GoogleSignInButton
                        onSuccess={async () => {
                            console.log('✅ Google OAuth complete, token set in localStorage');
                            // Force a small delay to ensure localStorage is synced
                            await new Promise(resolve => setTimeout(resolve, 50));
                            navigate('/admin', { replace: true });
                        }}
                        onError={(error: string) => {
                            setError(error);
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default AdminLogin;
